import { useState, useEffect } from 'react';
import { X, User, Mail, Phone, Stethoscope, DollarSign, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import { doctorsApi, Doctor, UpdateDoctorRequest } from '../../lib/api';

interface EditDoctorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDoctorUpdated: () => void;
  doctor: Doctor | null;
}

const EditDoctorModal = ({ isOpen, onClose, onDoctorUpdated, doctor }: EditDoctorModalProps) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<UpdateDoctorRequest>({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    licenseNumber: '',
    specialization: 'Cardiology',
    department: 'Internal Medicine',
    consultationFee: 100
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when doctor changes or modal opens
  useEffect(() => {
    if (doctor && isOpen) {
      setFormData({
        email: doctor.user.email,
        firstName: doctor.user.firstName,
        lastName: doctor.user.lastName,
        phone: doctor.user.phone || '',
        licenseNumber: doctor.licenseNumber,
        specialization: doctor.specialization,
        department: doctor.department,
        consultationFee: doctor.consultationFee
      });
      setCurrentStep(1);
      setErrors({});
    }
  }, [doctor, isOpen]);

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 1) {
      if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
      if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
      if (!formData.email.trim()) newErrors.email = 'Email is required';
      if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
      
      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (formData.email && !emailRegex.test(formData.email)) {
        newErrors.email = 'Invalid email format';
      }
    }

    if (step === 2) {
      if (!formData.licenseNumber.trim()) newErrors.licenseNumber = 'License number is required';
      if (!formData.specialization.trim()) newErrors.specialization = 'Specialization is required';
      if (!formData.department.trim()) newErrors.department = 'Department is required';
      if (formData.consultationFee <= 0) newErrors.consultationFee = 'Consultation fee must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
    setErrors({});
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep) || !doctor) return;

    setLoading(true);
    try {
      await doctorsApi.updateDoctor(doctor.id, formData);
      toast.success('Doctor updated successfully!');
      onDoctorUpdated();
      onClose();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to update doctor';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string | number) => {
    setFormData((prev: UpdateDoctorRequest) => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev: Record<string, string>) => ({ ...prev, [field]: '' }));
    }
  };

  if (!isOpen || !doctor) return null;

  const steps = [
    { number: 1, title: 'Personal Info', icon: User },
    { number: 2, title: 'Professional Info', icon: Stethoscope }
  ];

  const specializationOptions = [
    'Cardiology',
    'Neurology',
    'Orthopedics',
    'Pediatrics',
    'Dermatology',
    'Psychiatry',
    'Radiology',
    'Emergency Medicine',
    'Internal Medicine',
    'Surgery',
    'Gynecology',
    'Urology',
    'Ophthalmology',
    'ENT',
    'Anesthesiology'
  ];

  const departmentOptions = [
    'Internal Medicine',
    'Surgery',
    'Pediatrics',
    'Emergency',
    'Radiology',
    'Laboratory',
    'Neuroscience',
    'Cardiology',
    'Orthopedics',
    'Psychiatry',
    'Dermatology',
    'Gynecology',
    'Urology',
    'Ophthalmology',
    'ENT'
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            Edit Doctor - Dr. {doctor.user.firstName} {doctor.user.lastName}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b bg-gray-50">
          <div className="flex items-center justify-center space-x-8">
            {steps.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = currentStep === step.number;
              const isCompleted = currentStep > step.number;
              
              return (
                <div key={step.number} className="flex items-center">
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                    isActive ? 'bg-primary-600 text-white' :
                    isCompleted ? 'bg-green-600 text-white' :
                    'bg-gray-300 text-gray-600'
                  }`}>
                    <StepIcon className="h-4 w-4" />
                  </div>
                  <span className={`ml-2 text-sm font-medium ${
                    isActive ? 'text-primary-600' :
                    isCompleted ? 'text-green-600' :
                    'text-gray-500'
                  }`}>
                    {step.title}
                  </span>
                  {index < steps.length - 1 && (
                    <div className={`mx-8 h-px w-16 ${
                      isCompleted ? 'bg-green-600' : 'bg-gray-300'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Form Content */}
        <div className="p-6 overflow-y-auto max-h-96">
          {/* Step 1: Personal Information */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">First Name *</label>
                  <input
                    type="text"
                    className={`input ${errors.firstName ? 'input-error' : ''}`}
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    placeholder="Enter first name"
                  />
                  {errors.firstName && <p className="form-error">{errors.firstName}</p>}
                </div>
                <div>
                  <label className="form-label">Last Name *</label>
                  <input
                    type="text"
                    className={`input ${errors.lastName ? 'input-error' : ''}`}
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    placeholder="Enter last name"
                  />
                  {errors.lastName && <p className="form-error">{errors.lastName}</p>}
                </div>
              </div>

              <div>
                <label className="form-label">Email *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <input
                    type="email"
                    className={`input pl-10 ${errors.email ? 'input-error' : ''}`}
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="Enter email address"
                  />
                </div>
                {errors.email && <p className="form-error">{errors.email}</p>}
              </div>

              <div>
                <label className="form-label">Phone *</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <input
                    type="tel"
                    className={`input pl-10 ${errors.phone ? 'input-error' : ''}`}
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="Enter phone number"
                  />
                </div>
                {errors.phone && <p className="form-error">{errors.phone}</p>}
              </div>
            </div>
          )}

          {/* Step 2: Professional Information */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <div>
                <label className="form-label">License Number *</label>
                <div className="relative">
                  <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <input
                    type="text"
                    className={`input pl-10 ${errors.licenseNumber ? 'input-error' : ''}`}
                    value={formData.licenseNumber}
                    onChange={(e) => handleInputChange('licenseNumber', e.target.value)}
                    placeholder="Enter medical license number"
                  />
                </div>
                {errors.licenseNumber && <p className="form-error">{errors.licenseNumber}</p>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Specialization *</label>
                  <select
                    className={`input ${errors.specialization ? 'input-error' : ''}`}
                    value={formData.specialization}
                    onChange={(e) => handleInputChange('specialization', e.target.value)}
                  >
                    {specializationOptions.map(spec => (
                      <option key={spec} value={spec}>{spec}</option>
                    ))}
                  </select>
                  {errors.specialization && <p className="form-error">{errors.specialization}</p>}
                </div>
                <div>
                  <label className="form-label">Department *</label>
                  <select
                    className={`input ${errors.department ? 'input-error' : ''}`}
                    value={formData.department}
                    onChange={(e) => handleInputChange('department', e.target.value)}
                  >
                    {departmentOptions.map(dept => (
                      <option key={dept} value={dept}>{dept}</option>
                    ))}
                  </select>
                  {errors.department && <p className="form-error">{errors.department}</p>}
                </div>
              </div>

              <div>
                <label className="form-label">Consultation Fee (USD) *</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <input
                    type="number"
                    min="1"
                    step="0.01"
                    className={`input pl-10 ${errors.consultationFee ? 'input-error' : ''}`}
                    value={formData.consultationFee}
                    onChange={(e) => handleInputChange('consultationFee', parseFloat(e.target.value) || 0)}
                    placeholder="Enter consultation fee"
                  />
                </div>
                {errors.consultationFee && <p className="form-error">{errors.consultationFee}</p>}
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex">
                  <Stethoscope className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-amber-800">
                      Account Information
                    </h3>
                    <div className="mt-2 text-sm text-amber-700">
                      <p>Email changes will affect the doctor's login credentials. License number changes should be verified with administration.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          <div>
            {currentStep > 1 && (
              <button
                onClick={handlePrevious}
                className="btn-secondary"
                disabled={loading}
              >
                Previous
              </button>
            )}
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            
            {currentStep < 2 ? (
              <button
                onClick={handleNext}
                className="btn-primary"
                disabled={loading}
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                className="btn-primary"
                disabled={loading}
              >
                {loading ? 'Updating Doctor...' : 'Update Doctor'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditDoctorModal; 